import toast from 'react-hot-toast';
import useSubscriptionStore from '../store/useSubscriptionStore';
import { subscriptionApi } from './api';
import { isNativeIOS, initializeIAP, purchasePlan } from './iap';
import { getAuthToken } from './auth';

const GRACE_PERIOD_DAYS = 3;

// CdvPurchase v13 cancel codes — ALL THREE must be treated as a silent cancel.
// 6777006 (PAYMENT_CANCELLED) is Apple's real SKErrorPaymentCancelled mapping and was
// missing here; handling only 6777010 is what got BabyTrack v1.0 build 31 rejected under
// Guideline 2.1(b) ("displayed an error message when [we] attempted to purchase a
// subscription") — a reviewer who dismisses the payment sheet must see nothing at all.
// See shared-docs/in-app-purchases.md, "User-Initiated Cancel".
const CANCEL_CODES = new Set([
  6777006, // PAYMENT_CANCELLED — Apple's real SKErrorPaymentCancelled mapping
  6777003, // PURCHASE — generic code seen on legacy iPadOS sandbox cancels
  6777010, // UNKNOWN — Ask-to-Buy deferral / unknown failure on cancel
]);

export const DEFAULT_LIMITS = {
  recipes: { soft: 8, grace: 11, hard: 12 },
  favorites: { soft: 10, grace: 13, hard: 15 },
  categories: 3,
  groceryLists: 1,
  cookingSessionsPerWeek: 1,
  mealPlanWeeks: 'current',
};

export async function fetchSubscriptionStatus() {
  const store = useSubscriptionStore.getState();
  try {
    const data = await subscriptionApi.getStatus({ silent: true });
    store.setSubscriptionData(data);
    return data;
  } catch (err) {
    console.warn('Failed to fetch subscription status:', err.message);
    const cached = store.subscriptionData;
    if (cached?.subscriptionExpiresAt) {
      const expiresAt = new Date(cached.subscriptionExpiresAt);
      const graceDate = new Date(expiresAt);
      graceDate.setDate(graceDate.getDate() + GRACE_PERIOD_DAYS);
      if (new Date() > graceDate) {
        store.setSubscriptionData({ ...cached, tier: 'free', limits: DEFAULT_LIMITS });
      }
    }
    return null;
  }
}

function onPurchaseVerified(result) {
  if (!result || !result.success) return;
  const store = useSubscriptionStore.getState();
  const updated = {
    ...(store.subscriptionData || {}),
    tier: 'premium',
    subscription: true,
    subscriptionPlan: result.plan,
    subscriptionExpiresAt: result.currentPeriodEnd,
  };
  store.setSubscriptionData(updated);
  store.setShowUpgradeModal(false);
  store.setPurchasing(false);
  toast.success('Welcome to Premium!');
}

export async function initIAP() {
  const store = useSubscriptionStore.getState();
  store.setIapError(null);
  const result = await initializeIAP(onPurchaseVerified, getAuthToken);
  if (result.success) {
    store.setIapReady(true);
    store.setIapError(null);
  } else if (result.error !== 'not-ios') {
    store.setIapError(result.error);
  }
  return result;
}

export function promptUpgrade(context) {
  const store = useSubscriptionStore.getState();
  store.setUpgradeContext(context);
  store.setShowUpgradeModal(true);
}

// iOS-only purchase entry point. Web checkout (Stripe) has been removed —
// Premium is available exclusively via Apple In-App Purchase in the iOS app.
export async function startIapPurchase(plan) {
  if (!isNativeIOS()) return;
  const store = useSubscriptionStore.getState();
  store.setPurchaseError(null);
  store.setPurchasing(true);
  try {
    console.log('[IAP] Starting purchase for plan:', plan);
    await purchasePlan(plan);
    // CdvPurchase fires the approved handler asynchronously — keep purchasing=true
    // until verifyAppleIap callback flips it. 10s safety timeout for stalls.
    setTimeout(() => {
      if (useSubscriptionStore.getState().purchasing) {
        fetchSubscriptionStatus();
        useSubscriptionStore.getState().setShowUpgradeModal(false);
        useSubscriptionStore.getState().setPurchasing(false);
      }
    }, 10000);
  } catch (err) {
    console.error('[IAP] Purchase error:', err?.code, err?.message, err);
    const cancelled = CANCEL_CODES.has(err?.code)
      || err?.code === 'E_USER_CANCELLED'
      || (err?.message && err.message.toLowerCase().includes('cancel'));
    if (!cancelled) {
      const msg = err.message || 'Purchase failed. Please try again.';
      store.setPurchaseError(msg);
      toast.error(msg);
    }
    store.setPurchasing(false);
  }
}

function getCurrentWeekStart() {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - (day === 0 ? 6 : day - 1);
  const monday = new Date(now);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday.toISOString().split('T')[0];
}

export function makeGates({ tier, limits, usage }) {
  const isPremium = tier === 'premium';
  return {
    canAddRecipe: () => {
      if (isPremium) return { allowed: true, warning: null };
      const count = usage.recipeCount;
      if (count >= limits.recipes.hard) return { allowed: false, warning: 'hard' };
      if (count >= limits.recipes.grace) return { allowed: true, warning: 'grace' };
      if (count >= limits.recipes.soft) return { allowed: true, warning: 'soft' };
      return { allowed: true, warning: null };
    },
    canSaveFavorite: () => {
      if (isPremium) return { allowed: true, warning: null };
      const count = usage.recipeCount;
      if (count >= limits.favorites.hard) return { allowed: false, warning: 'hard' };
      if (count >= limits.favorites.grace) return { allowed: true, warning: 'grace' };
      if (count >= limits.favorites.soft) return { allowed: true, warning: 'soft' };
      return { allowed: true, warning: null };
    },
    canAccessMealPlanWeek: (weekStart) => isPremium || weekStart === getCurrentWeekStart(),
    canUseCookingMode: () => isPremium || usage.cookingModeSessionsThisWeek < limits.cookingSessionsPerWeek,
    canAddCategory: () => isPremium || usage.customCategoryCount < limits.categories,
    getRecipeWarningLevel: () => {
      if (isPremium) return null;
      const count = usage.recipeCount;
      if (count >= limits.recipes.hard) return 'hard';
      if (count >= limits.recipes.grace) return 'grace';
      if (count >= limits.recipes.soft) return 'soft';
      return null;
    },
  };
}
